import { useState } from "react";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSTextField from "views/system-design/DSTextField";
import DSButton from "views/system-design/DSButton";
import PersonIcon from "@mui/icons-material/Person";
import { Ticket } from "../../types";
import { useTickets } from "../../context/TicketsContext";
import { generateId, formatDate } from "../../utils";
import firebaseService from "firebase_service/firebaseService";

interface TicketCommentsProps {
  ticket: Ticket;
}

export default function TicketComments({ ticket }: TicketCommentsProps) {
  const { addComment } = useTickets();
  const [text, setText] = useState("");

  const handleAdd = () => {
    const trimmed = text.trim();
    if (!trimmed) return;

    addComment(ticket.id, {
      id: generateId(),
      author: firebaseService.getUserName() ?? "Admin",
      text: trimmed,
      createdAt: new Date().toISOString(),
    });
    setText("");
  };

  return (
    <DSBox sx={{ mt: 3 }}>
      <DSTypography
        variant="subtitle2"
        sx={{ color: "text.secondary", mb: 1.5 }}
      >
        Comments ({ticket.comments.length})
      </DSTypography>

      <DSBox sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
        {ticket.comments.map((comment) => (
          <DSBox
            key={comment.id}
            sx={{ p: 1.5, bgcolor: "white.5", borderRadius: 1 }}
          >
            <DSBox
              sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
            >
              <PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <DSTypography variant="caption" sx={{ fontWeight: 600 }}>
                {comment.author}
              </DSTypography>
              <DSTypography
                variant="caption"
                sx={{ color: "text.secondary", ml: 1 }}
              >
                {formatDate(comment.createdAt)}
              </DSTypography>
            </DSBox>
            <DSTypography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {comment.text}
            </DSTypography>
          </DSBox>
        ))}
      </DSBox>

      <DSBox sx={{ display: "flex", gap: 1 }}>
        <DSTextField
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder="Add a comment..."
          size="small"
          fullWidth
          multiline
          minRows={1}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              handleAdd();
            }
          }}
        />
        <DSButton size="small" onClick={handleAdd} disabled={!text.trim()}>
          Add
        </DSButton>
      </DSBox>
    </DSBox>
  );
}
